import { Injectable } from '@angular/core'
import {
  AgendaDateRange,
  AgendaEventMove,
  AgendaFitSlot,
  AgendaSchedule,
  AppointmentSlot,
  BlockedSlot,
  CalendarContext,
  CalendarSlot,
  CalendarSlotBase,
  MovementContext,
  MovementEvaluationResult,
  FreeSlot,
  MoveSlotResult,
  ScheduleTimeGrid,
  isAppointmentFitIn,
  ScheduleGridResponse,
  ScheduleGridSlotResponse,
  getStatusColor,
} from './components/agenda-calendar/agenda-calendar.models'

/**
 * View-agnostic schedule domain logic. Used by any view that renders schedules
 * (FullCalendar-based agenda-calendar, dense grid, etc.).
 *
 * Pure logic only — no FullCalendar imports, no view-specific config.
 */
@Injectable()
export class ScheduleDomainService {
  // Permission flags consumed by moveSlot to enforce fit-in / transfer rules across views.
  // Each view (agenda-calendar, dense-schedule-grid) sets these from UserPermissionService
  // so a single source of truth decides what's blocked.
  allowFitIn = true
  allowTransfer = true

  // --- API response mapping ---

  mapGridResponse(
    grids: ScheduleGridResponse[],
    schedules: AgendaSchedule[],
    specificDates?: string[],
  ): {
    scheduleGrids: ScheduleTimeGrid[]
    calendarSlots: CalendarSlot[]
    blockedSlots: BlockedSlot[]
  } {
    const scheduleGrids: ScheduleTimeGrid[] = []
    const calendarSlots: CalendarSlot[] = []
    const blockedSlots: BlockedSlot[] = []
    const allowedDates = specificDates?.length
      ? new Set(specificDates)
      : null

    for (const grid of grids) {
      const gridSlots: ScheduleTimeGrid['slots'] = []

      for (const day of grid.days) {
        if (allowedDates && !allowedDates.has(day.date)) continue

        for (const slot of day.slots) {
          if (slot.occupiedSlots?.length) {
            for (const occupied of slot.occupiedSlots) {
              gridSlots.push({
                start: occupied.startDateTime,
                end: occupied.endDateTime,
                observation: slot.templateObservation,
                services: slot.templateServices,
              })
            }
          } else {
            gridSlots.push({
              start: slot.startDateTime,
              end: slot.endDateTime,
              observation: slot.templateObservation,
              services: slot.templateServices,
            })
          }

          const mapped = this.mapGridSlot(slot, grid.scheduleId)
          if (mapped) {
            calendarSlots.push(mapped)
            if (mapped.type === 'blocked') {
              blockedSlots.push(mapped)
            }
          }
        }
      }

      const schedule = schedules.find((s) => s.id === grid.scheduleId)
      scheduleGrids.push({
        scheduleId: grid.scheduleId,
        slots: gridSlots,
        generatePartialFreeSlots: schedule?.generatePartialFreeSlots,
        minMinutesForFreeSlot: schedule?.minMinutesForFreeSlot,
      })
    }

    const { calendarSlots: filteredSlots, blockedSlots: filteredBlocks } =
      this.dropExtendedTimeBlocksOverAppointments(calendarSlots, blockedSlots)

    return { scheduleGrids, calendarSlots: filteredSlots, blockedSlots: filteredBlocks }
  }

  private static readonly EXTENDED_TIME_OBSERVATION = 'Agendamento anterior com tempo estendido.'

  /**
   * Remove blocked slots whose observation marks them as the "extended-time" filler
   * created when an appointment is artificially extended over later grid slots.
   * They become redundant once the front merges contiguous appointments and would
   * otherwise paint a "Bloqueado" stripe on top of a real card.
   */
  private dropExtendedTimeBlocksOverAppointments(
    slots: CalendarSlot[],
    blocked: BlockedSlot[],
  ): { calendarSlots: CalendarSlot[]; blockedSlots: BlockedSlot[] } {
    const appointmentsBySchedule = new Map<number, AppointmentSlot[]>()
    for (const slot of slots) {
      if (slot.type !== 'appointment') continue
      if (!appointmentsBySchedule.has(slot.scheduleId)) appointmentsBySchedule.set(slot.scheduleId, [])
      appointmentsBySchedule.get(slot.scheduleId)!.push(slot)
    }

    const isShadowed = (b: BlockedSlot): boolean => {
      if (b.observation !== ScheduleDomainService.EXTENDED_TIME_OBSERVATION) return false
      const apts = appointmentsBySchedule.get(b.scheduleId) ?? []
      const bStart = this.toMs(b.start)
      const bEnd = this.toMs(b.end)
      for (const apt of apts) {
        if (this.toMs(apt.start) < bEnd && this.toMs(apt.end) > bStart) return true
      }
      return false
    }

    const calendarSlots = slots.filter((s) => s.type !== 'blocked' || !isShadowed(s))
    const blockedSlots = blocked.filter((b) => !isShadowed(b))
    return { calendarSlots, blockedSlots }
  }

  private mapGridSlot(slot: ScheduleGridSlotResponse, scheduleId: number): CalendarSlot | null {
    switch (slot.type) {
      case 'free':
        return {
          type: 'free',
          scheduleId,
          start: slot.startDateTime,
          end: slot.endDateTime,
          observation: slot.templateObservation,
          services: slot.templateServices,
          lockUsers: slot.lock?.users ?? [],
        }

      case 'appointment':
        if (!slot.appointment) return null
        return {
          type: 'appointment',
          scheduleId,
          start: slot.startDateTime,
          end: slot.endDateTime,
          id: slot.appointment.id,
          isFitIn: slot.appointment.isExtraSlot,
          observation: slot.appointment.observation,
          status: slot.appointment.status,
          patient: slot.appointment.patient,
          insurance: slot.appointment.insurance,
          procedures: slot.appointment.procedures,
          performingDoctor: slot.appointment.performingDoctor,
          requestingDoctor: slot.appointment.requestingDoctor,
          slotId: slot.slotId,
          lockUsers: slot.lock?.users ?? [],
        }

      case 'blocked':
        if (!slot.block) return null
        return {
          type: 'blocked',
          scheduleId,
          start: slot.startDateTime,
          end: slot.endDateTime,
          observation: slot.block.observation,
          lockUsers: slot.lock?.users ?? [],
        }

      default:
        return null
    }
  }

  // --- Time range / date list ---

  calculateTimeRange(grids: ScheduleTimeGrid[], stepSeconds?: number): { minTime: string; maxTime: string } {
    const defaultMin = '06:00:00'
    const defaultMax = '22:00:00'

    const allSlots = grids.flatMap((g) => g.slots)
    if (allSlots.length === 0) {
      return { minTime: defaultMin, maxTime: defaultMax }
    }

    const times = allSlots.flatMap((slot) => [
      this.extractTime(slot.start),
      this.extractTime(slot.end)
    ])

    times.sort()

    let minTime = times[0]
    let maxTime = times[times.length - 1]

    if (stepSeconds && stepSeconds > 0) {
      minTime = this.alignTimeToStep(minTime, stepSeconds, 'floor')
      maxTime = this.alignTimeToStep(maxTime, stepSeconds, 'ceil')
    }

    return { minTime, maxTime }
  }

  private alignTimeToStep(time: string, stepSeconds: number, mode: 'floor' | 'ceil'): string {
    const [h, m, s] = time.split(':').map(Number)
    const total = h * 3600 + m * 60 + (s ?? 0)
    const aligned = mode === 'floor'
      ? Math.floor(total / stepSeconds) * stepSeconds
      : Math.ceil(total / stepSeconds) * stepSeconds
    const clamped = Math.max(0, Math.min(86400, aligned))
    const hh = String(Math.floor(clamped / 3600)).padStart(2, '0')
    const mm = String(Math.floor((clamped % 3600) / 60)).padStart(2, '0')
    const ss = String(clamped % 60).padStart(2, '0')
    return `${hh}:${mm}:${ss}`
  }

  getDateList(dateRange?: AgendaDateRange): string[] {
    if (!dateRange) return ['']

    if (dateRange.specificDates?.length) {
      return [...dateRange.specificDates].sort()
    }

    if (dateRange.start === dateRange.end) return [dateRange.start]
    const dates: string[] = []
    const current = new Date(dateRange.start + 'T12:00:00')
    const end = new Date(dateRange.end + 'T12:00:00')
    while (current <= end) {
      dates.push(current.toISOString().split('T')[0])
      current.setDate(current.getDate() + 1)
    }
    return dates
  }

  // --- Free slot derivation ---

  isPartialFreeSlotAllowed(startMs: number, endMs: number, scheduleGrid: ScheduleTimeGrid): boolean {
    if (scheduleGrid.generatePartialFreeSlots === false) return false
    const durationMinutes = (endMs - startMs) / 60000
    return durationMinutes >= (scheduleGrid.minMinutesForFreeSlot ?? 5)
  }

  deriveFreeSlots(scheduleGrids: ScheduleTimeGrid[], appointments: AppointmentSlot[], blockedSlots: BlockedSlot[] = []): FreeSlot[] {
    const freeSlots: FreeSlot[] = []

    for (const scheduleGrid of scheduleGrids) {
      const occupiedSlots: CalendarSlotBase[] = [
        ...appointments.filter((a) => a.scheduleId === scheduleGrid.scheduleId),
        ...blockedSlots.filter((b) => b.scheduleId === scheduleGrid.scheduleId),
      ].sort((a, b) => this.toMs(a.start) - this.toMs(b.start))

      for (const slot of scheduleGrid.slots) {
        const slotStart = this.toMs(slot.start)
        const slotEnd = this.toMs(slot.end)

        const slotBase: CalendarSlotBase = { scheduleId: scheduleGrid.scheduleId, start: slot.start, end: slot.end, lockUsers: [] }
        const overlapping = occupiedSlots.filter((a) => this.overlaps(a, slotBase))

        if (overlapping.length === 0) {
          freeSlots.push({ type: 'free', scheduleId: scheduleGrid.scheduleId, start: slot.start, end: slot.end, observation: slot.observation, services: slot.services, lockUsers: [] })
          continue
        }

        let cursor = slotStart
        for (const apt of overlapping) {
          const aptStart = this.toMs(apt.start)
          const aptEnd = this.toMs(apt.end)

          const gapEnd = Math.min(aptStart, slotEnd)
          if (aptStart > cursor && this.isPartialFreeSlotAllowed(cursor, gapEnd, scheduleGrid)) {
            freeSlots.push({ type: 'free', scheduleId: scheduleGrid.scheduleId, start: this.toLocalIso(cursor), end: this.toLocalIso(gapEnd), originalStart: slot.start, originalEnd: slot.end, observation: slot.observation, services: slot.services, lockUsers: [] })
          }
          cursor = Math.max(cursor, Math.min(aptEnd, slotEnd))
        }

        if (cursor < slotEnd && this.isPartialFreeSlotAllowed(cursor, slotEnd, scheduleGrid)) {
          freeSlots.push({ type: 'free', scheduleId: scheduleGrid.scheduleId, start: this.toLocalIso(cursor), end: this.toLocalIso(slotEnd), originalStart: slot.start, originalEnd: slot.end, observation: slot.observation, services: slot.services, lockUsers: [] })
        }
      }
    }

    return freeSlots
  }

  // --- Move evaluation ---

  moveSlot(
    appointment: AppointmentSlot,
    oldScheduleId: number,
    newScheduleId: number,
    newStart: string,
    newEnd: string,
    ctx: CalendarContext,
  ): MoveSlotResult {
    if (!this.allowTransfer && oldScheduleId !== newScheduleId) {
      return { action: 'blocked', error: 'Você não tem permissão para realizar transferências' }
    }

    const appointmentDurationMs = this.toMs(appointment.end) - this.toMs(appointment.start)
    const rawTarget: CalendarSlotBase = { scheduleId: newScheduleId, start: newStart, end: newEnd, lockUsers: [] }

    const { target, evaluation } = this.evaluatePlacement(ctx.slots, rawTarget, appointment, appointmentDurationMs)

    const moveData: AgendaEventMove = {
      appointment,
      oldScheduleId,
      newScheduleId,
      oldStart: appointment.start,
      oldEnd: appointment.end,
      newStart: target.start,
      newEnd: target.end,
    }

    switch (evaluation.scenario) {
      case 'blocked':
        return { action: 'blocked', error: 'Não é possível mover para este horário' }

      case 'exact':
        return { action: 'moved', moveData }

      case 'slot-bigger':
        if (evaluation.containingSlot) {
          moveData.newStart = evaluation.containingSlot.start
          moveData.newEnd = evaluation.containingSlot.end
        }
        return { action: 'moved', moveData }

      case 'outside-slot':
        if (!this.allowFitIn) {
          return { action: 'blocked', error: 'Você não tem permissão para realizar encaixes' }
        }
        return this.resolveOutsideSlotMove(evaluation, moveData, appointmentDurationMs, appointment, newScheduleId, ctx)

      case 'mixed-partial':
        if (!this.allowFitIn && !evaluation.availableFreeSlot && !evaluation.nearbyAlternativeSlot) {
          return { action: 'blocked', error: 'Você não tem permissão para realizar encaixes' }
        }
        return {
          action: 'needs-confirmation',
          moveData,
          coveredMinutes: evaluation.coveredMinutes,
          totalMinutes: evaluation.totalMinutes,
          availableFreeSlot: evaluation.availableFreeSlot,
          canKeepOriginalDuration: false,
          nearbyAlternativeSlot: evaluation.nearbyAlternativeSlot,
        }
    }
  }

  updateSlotTime(
    targetEvent: AgendaEventMove,
    timeChanges: { start?: string; end?: string },
    { slots }: CalendarContext,
  ): { error?: string; evaluation?: MovementEvaluationResult } {
    if (timeChanges.start) {
      const [hours, minutes] = timeChanges.start.split(':')
      const current = new Date(targetEvent.newStart)
      current.setHours(Number(hours), Number(minutes), 0, 0)
      targetEvent.newStart = this.toLocalIso(current.getTime())
    }

    if (timeChanges.end) {
      const [hours, minutes] = timeChanges.end.split(':')
      const current = new Date(targetEvent.newEnd)
      current.setHours(Number(hours), Number(minutes), 0, 0)
      targetEvent.newEnd = this.toLocalIso(current.getTime())
    }

    const startMs = this.toMs(targetEvent.newStart)
    const endMs = this.toMs(targetEvent.newEnd)

    if (startMs >= endMs) {
      return { error: 'O horário de início deve ser anterior ao horário de fim' }
    }

    if ((endMs - startMs) / 60000 < 5) {
      return { error: 'A duração mínima do encaixe é de 5 minutos' }
    }

    const newTarget: CalendarSlotBase = {
      scheduleId: targetEvent.newScheduleId,
      start: targetEvent.newStart,
      end: targetEvent.newEnd,
      lockUsers: [],
    }
    const durationMs = endMs - startMs

    const { evaluation } = this.evaluatePlacement(slots, newTarget, targetEvent.appointment, durationMs)

    if (evaluation.scenario === 'blocked') {
      return { error: 'Este horário colide com outro agendamento' }
    }

    return { evaluation }
  }

  private evaluatePlacement(
    slots: CalendarSlot[],
    rawTarget: CalendarSlotBase,
    movingAppointment: AppointmentSlot,
    appointmentDurationMs: number,
  ): { target: CalendarSlotBase; evaluation: MovementEvaluationResult } {
    const ctx = this.gatherMovementContext(slots, rawTarget, movingAppointment, appointmentDurationMs)

    if (ctx.hasAppointmentCollision && ctx.freeSlots.length === 0) {
      return { target: rawTarget, evaluation: { scenario: 'blocked', remainingSlots: [], coveredMinutes: 0, totalMinutes: ctx.appointmentMinutes } }
    }

    if (ctx.hasAppointmentCollision && ctx.freeSlots.length > 0) {
      return this.evaluateMixedArea(ctx, slots)
    }

    if (ctx.slotsAtTarget.length === 0) {
      return { target: rawTarget, evaluation: { scenario: 'outside-slot', remainingSlots: [], coveredMinutes: 0, totalMinutes: ctx.appointmentMinutes } }
    }

    if (ctx.freeSlots.length === 0) {
      return { target: rawTarget, evaluation: { scenario: 'blocked', remainingSlots: [], coveredMinutes: 0, totalMinutes: ctx.appointmentMinutes } }
    }

    const target = this.snapToNearestFreeSlot(slots, rawTarget, appointmentDurationMs)
    const snappedCtx = this.gatherMovementContext(slots, target, movingAppointment, appointmentDurationMs)

    if (snappedCtx.freeSlots.length === 1) {
      return { target, evaluation: this.evaluateSingleFreeSlot(snappedCtx, slots) }
    }

    return { target, evaluation: this.evaluateMultipleFreeSlots(snappedCtx) }
  }

  private gatherMovementContext(
    slots: CalendarSlot[],
    rawTarget: CalendarSlotBase,
    movingAppointment: AppointmentSlot,
    appointmentDurationMs: number,
  ): MovementContext {
    const appointmentMinutes = appointmentDurationMs / 60000

    const isMovingSlot = (s: CalendarSlot) =>
      s.type === 'appointment' && s.id === movingAppointment.id

    const scheduleSlots = slots.filter((s) => s.scheduleId === rawTarget.scheduleId)
    const slotsAtTarget = scheduleSlots
      .filter((s) => this.overlaps(s, rawTarget) && !isMovingSlot(s))
      .sort((a, b) => this.compareTime(a.start, b.start))

    const hasAppointmentCollision = slotsAtTarget.some((s) => s.type === 'appointment')

    const rawFreeSlots = slotsAtTarget.filter((s): s is FreeSlot => s.type === 'free')
    const freeSlots = rawFreeSlots.length > 0 ? this.mergeConsecutiveFreeSlots(rawFreeSlots) : []

    return { appointmentMinutes, appointmentDurationMs, slotsAtTarget, freeSlots, target: rawTarget, rawTarget, movingAppointment, hasAppointmentCollision }
  }

  private evaluateSingleFreeSlot(ctx: MovementContext, allSlots: CalendarSlot[] = []): MovementEvaluationResult {
    const slot = ctx.freeSlots[0]
    const slotMinutes = this.diffMinutes(slot.start, slot.end)

    if (slotMinutes === ctx.appointmentMinutes) {
      return { scenario: 'exact', remainingSlots: [], coveredMinutes: ctx.appointmentMinutes, totalMinutes: ctx.appointmentMinutes }
    }

    if (slotMinutes > ctx.appointmentMinutes) {
      const remainingSlots: AgendaFitSlot[] = []
      if (this.isBefore(slot.start, ctx.target.start)) {
        remainingSlots.push({ scheduleId: ctx.target.scheduleId, start: slot.start, end: ctx.target.start })
      }
      if (this.isAfter(slot.end, ctx.target.end)) {
        remainingSlots.push({ scheduleId: ctx.target.scheduleId, start: ctx.target.end, end: slot.end })
      }

      return { scenario: 'slot-bigger', remainingSlots, coveredMinutes: ctx.appointmentMinutes, totalMinutes: ctx.appointmentMinutes, containingSlot: slot }
    }

    if (slot.originalStart && slot.originalEnd) {
      const originalBase: CalendarSlotBase = { scheduleId: ctx.target.scheduleId, start: slot.originalStart, end: slot.originalEnd, lockUsers: [] }
      const otherAptsInOriginal = allSlots.filter((s): s is AppointmentSlot =>
        s.type === 'appointment' && s.id !== ctx.movingAppointment.id
        && s.scheduleId === ctx.target.scheduleId && this.overlaps(s, originalBase),
      )

      if (otherAptsInOriginal.length === 0) {
        const originalMinutes = this.diffMinutes(slot.originalStart, slot.originalEnd)
        if (originalMinutes >= ctx.appointmentMinutes) {
          const expandedSlot: FreeSlot = { ...slot, start: slot.originalStart, end: slot.originalEnd }
          return { scenario: 'slot-bigger', remainingSlots: [], coveredMinutes: ctx.appointmentMinutes, totalMinutes: ctx.appointmentMinutes, containingSlot: expandedSlot }
        }
      }
    }

    return {
      scenario: 'outside-slot', remainingSlots: [], coveredMinutes: slotMinutes, totalMinutes: ctx.appointmentMinutes,
      availableFreeSlot: { scheduleId: ctx.target.scheduleId, start: slot.start, end: slot.end, lockUsers: [] },
    }
  }

  private evaluateMultipleFreeSlots(ctx: MovementContext): MovementEvaluationResult {
    let coveredMinutes = 0
    let largestSlot = ctx.freeSlots[0]

    for (const s of ctx.freeSlots) {
      const overlapStart = this.isAfter(s.start, ctx.target.start) ? s.start : ctx.target.start
      const overlapEnd = this.isBefore(s.end, ctx.target.end) ? s.end : ctx.target.end
      coveredMinutes += this.diffMinutes(overlapStart, overlapEnd)
      if (this.diffMinutes(s.start, s.end) > this.diffMinutes(largestSlot.start, largestSlot.end)) {
        largestSlot = s
      }
    }

    return {
      scenario: 'outside-slot', remainingSlots: [], coveredMinutes, totalMinutes: ctx.appointmentMinutes,
      availableFreeSlot: { scheduleId: ctx.target.scheduleId, start: largestSlot.start, end: largestSlot.end, lockUsers: [] },
    }
  }

  private evaluateMixedArea(ctx: MovementContext, slots: CalendarSlot[]): { target: CalendarSlotBase; evaluation: MovementEvaluationResult } {
    let freeMinutesAtRaw = 0
    for (const fs of ctx.freeSlots) {
      const overlapStart = this.isAfter(fs.start, ctx.rawTarget.start) ? fs.start : ctx.rawTarget.start
      const overlapEnd = this.isBefore(fs.end, ctx.rawTarget.end) ? fs.end : ctx.rawTarget.end
      if (this.isBefore(overlapStart, overlapEnd)) {
        freeMinutesAtRaw += this.diffMinutes(overlapStart, overlapEnd)
      }
    }

    if (freeMinutesAtRaw >= ctx.appointmentMinutes) {
      const target = this.snapToNearestFreeSlot(slots, ctx.rawTarget, ctx.appointmentDurationMs)
      const snappedCtx = this.gatherMovementContext(slots, target, ctx.movingAppointment, ctx.appointmentDurationMs)

      if (snappedCtx.freeSlots.length === 1) {
        return { target, evaluation: this.evaluateSingleFreeSlot(snappedCtx) }
      }
      return { target, evaluation: this.evaluateMultipleFreeSlots(snappedCtx) }
    }

    let largestSlot = ctx.freeSlots[0]
    for (const fs of ctx.freeSlots) {
      if (this.diffMinutes(fs.start, fs.end) > this.diffMinutes(largestSlot.start, largestSlot.end)) {
        largestSlot = fs
      }
    }

    const nearbyAlternative = this.findNearbyFittingSlot(
      slots, ctx.rawTarget, ctx.movingAppointment, ctx.appointmentMinutes,
    )

    return {
      target: ctx.rawTarget,
      evaluation: {
        scenario: 'mixed-partial',
        remainingSlots: [],
        coveredMinutes: freeMinutesAtRaw,
        totalMinutes: ctx.appointmentMinutes,
        availableFreeSlot: {
          scheduleId: ctx.rawTarget.scheduleId,
          start: largestSlot.start,
          end: largestSlot.end,
          lockUsers: [],
        },
        nearbyAlternativeSlot: nearbyAlternative ?? undefined,
      },
    }
  }

  private resolveOutsideSlotMove(
    evaluation: MovementEvaluationResult,
    moveData: AgendaEventMove,
    appointmentDurationMs: number,
    appointment: AppointmentSlot,
    newScheduleId: number,
    ctx: CalendarContext,
  ): MoveSlotResult {
    const isOutsideGrid = !evaluation.availableFreeSlot
    let canKeepOriginalDuration = isOutsideGrid

    if (!isOutsideGrid) {
      const freeSlot = evaluation.availableFreeSlot!
      const freeStart = this.toMs(freeSlot.start)
      const freeEnd = this.toMs(freeSlot.end)

      const hasCollision = (start: number, end: number) =>
        ctx.slots.some((s) => {
          if (s.type !== 'appointment' || s.id === appointment.id) return false
          if (s.scheduleId !== newScheduleId) return false
          const sStart = this.toMs(s.start)
          const sEnd = this.toMs(s.end)
          return sStart < end && sEnd > start
        })

      const downEnd = freeStart + appointmentDurationMs
      if (!hasCollision(freeStart, downEnd)) {
        canKeepOriginalDuration = true
        moveData.newStart = this.toLocalIso(freeStart)
        moveData.newEnd = this.toLocalIso(downEnd)
      } else {
        const upStart = freeEnd - appointmentDurationMs
        if (!hasCollision(upStart, freeEnd)) {
          canKeepOriginalDuration = true
          moveData.newStart = this.toLocalIso(upStart)
          moveData.newEnd = this.toLocalIso(freeEnd)
        }
      }
    }

    return {
      action: 'needs-confirmation',
      moveData,
      coveredMinutes: evaluation.coveredMinutes,
      totalMinutes: evaluation.totalMinutes,
      availableFreeSlot: evaluation.availableFreeSlot,
      canKeepOriginalDuration,
    }
  }

  private findNearbyFittingSlot(
    slots: CalendarSlot[],
    rawTarget: CalendarSlotBase,
    movingAppointment: AppointmentSlot,
    appointmentMinutes: number,
  ): CalendarSlotBase | null {
    const freeSlots = slots.filter(
      (s): s is FreeSlot => s.type === 'free' && s.scheduleId === rawTarget.scheduleId,
    )

    const sorted = [...freeSlots].sort((a, b) => this.compareTime(a.start, b.start))
    const merged = this.mergeConsecutiveFreeSlots(sorted)

    const targetMs = this.toMs(rawTarget.start)
    let bestSlot: CalendarSlotBase | null = null
    let bestDistance = Infinity

    for (const fs of merged) {
      if (fs.scheduleId === movingAppointment.scheduleId && this.overlaps(fs, movingAppointment)) continue

      const slotMinutes = this.diffMinutes(fs.start, fs.end)
      if (slotMinutes < appointmentMinutes) continue

      const distance = Math.abs(this.toMs(fs.start) - targetMs)
      if (distance < bestDistance) {
        bestDistance = distance
        bestSlot = { scheduleId: rawTarget.scheduleId, start: fs.start, end: fs.end, lockUsers: [] }
      }
    }

    return bestSlot
  }

  snapToNearestFreeSlot(
    slots: CalendarSlot[],
    target: CalendarSlotBase,
    appointmentDurationMs: number,
  ): CalendarSlotBase {
    const freeSlots = slots.filter(
      (s): s is FreeSlot => s.type === 'free' && s.scheduleId === target.scheduleId,
    )

    if (freeSlots.length === 0) return target

    const targetMs = this.toMs(target.start)
    let closest = freeSlots[0]
    let minDistance = Math.abs(this.toMs(closest.start) - targetMs)

    for (let i = 1; i < freeSlots.length; i++) {
      const distance = Math.abs(this.toMs(freeSlots[i].start) - targetMs)
      if (distance < minDistance) {
        minDistance = distance
        closest = freeSlots[i]
      }
    }

    const snappedStart = closest.start
    const snappedEnd = this.toLocalIso(this.toMs(snappedStart) + appointmentDurationMs)

    return { scheduleId: target.scheduleId, start: snappedStart, end: snappedEnd, lockUsers: [] }
  }

  // --- Apply move locally + re-derive free slots ---

  applyMove(
    moveData: AgendaEventMove,
    { slots, grids, blockedSlots }: CalendarContext,
    options?: { markAsFitIn?: boolean },
  ): CalendarSlot[] {
    const appointments = slots
      .filter((s): s is AppointmentSlot => s.type === 'appointment')
      .map((apt) => {
        if (apt.id === moveData.appointment.id) {
          const moved = { ...apt, scheduleId: moveData.newScheduleId, start: moveData.newStart, end: moveData.newEnd, lockUsers: [] }
          if (options?.markAsFitIn) {
            moved.slotId = undefined
          } else if (isAppointmentFitIn(apt)) {
            moved.slotId = -1
          }
          return moved
        }
        return apt
      })

    const oldFreeSlots = slots.filter((s): s is FreeSlot => s.type === 'free')
    const freeSlots = this.deriveFreeSlots(grids, appointments, blockedSlots)

    for (const free of freeSlots) {
      const prev = oldFreeSlots.find((o) => o.scheduleId === free.scheduleId && o.start === free.start)
      if (prev?.lockUsers.length) free.lockUsers = prev.lockUsers
    }

    return [...appointments, ...blockedSlots, ...freeSlots]
  }

  private mergeConsecutiveFreeSlots(slots: FreeSlot[]): FreeSlot[] {
    if (slots.length <= 1) return slots

    const sorted = [...slots].sort((a, b) => this.compareTime(a.start, b.start))
    const merged: FreeSlot[] = [{ ...sorted[0] }]

    for (let i = 1; i < sorted.length; i++) {
      const last = merged[merged.length - 1]
      if (this.isBeforeOrEqual(sorted[i].start, last.end)) {
        if (this.isAfter(sorted[i].end, last.end)) {
          last.end = sorted[i].end
        }
      } else {
        merged.push({ ...sorted[i] })
      }
    }

    return merged
  }

  // --- Resource ID helpers (schedule × date) ---

  resolveResourceId(slot: CalendarSlot, multiDay: boolean): string {
    if (!multiDay) return String(slot.scheduleId)
    const slotDate = slot.start.split('T')[0]
    return `${slot.scheduleId}-${slotDate}`
  }

  extractScheduleId(resourceId: string): number {
    const dashIndex = resourceId.indexOf('-')
    if (dashIndex === -1) return Number(resourceId)
    return Number(resourceId.substring(0, dashIndex))
  }

  extractDateFromResource(resourceId: string): string | null {
    const dashIndex = resourceId.indexOf('-')
    if (dashIndex === -1) return null
    return resourceId.substring(dashIndex + 1)
  }

  replaceDateInDateTime(isoDateTime: string, newDate: string): string {
    const timePart = isoDateTime.split('T')[1]
    if (timePart) return `${newDate}T${timePart}`
    return `${newDate}T00:00:00`
  }

  // --- Formatting (used in templates) ---

  formatTime(isoDateTime: string): string {
    const date = new Date(isoDateTime)
    return `${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}`
  }

  formatWeekday(isoDate: string, locale: 'pt-br' | 'en' | 'es'): string {
    const date = new Date(isoDate + 'T12:00:00')
    const weekday = date.toLocaleDateString(locale === 'pt-br' ? 'pt-BR' : locale === 'es' ? 'es-CL' : 'en-US', {
      weekday: 'long',
    })
    return weekday.charAt(0).toUpperCase() + weekday.slice(1)
  }

  formatDate(isoDate: string): string {
    const [, month, day] = isoDate.split('-')
    return `${day}/${month}`
  }

  // --- Appointment grouping ---

  /**
   * Merges consecutive appointments that share patient, status, schedule and have a start gap of <=60s.
   * Mirrors the dense-view rule. Free/blocked slots act as boundaries (preserved as-is).
   * Procedures are unioned (deduped by id). The merged card keeps the first appointment's id and stores
   * `mergedIds` in extendedProps. Grouping cannot be undone from the UI.
   */
  mergeContiguousAppointments(slots: CalendarSlot[], scheduleGrids: ScheduleTimeGrid[] = []): CalendarSlot[] {
    const bySchedule = new Map<number, CalendarSlot[]>()
    for (const slot of slots) {
      if (!bySchedule.has(slot.scheduleId)) bySchedule.set(slot.scheduleId, [])
      bySchedule.get(slot.scheduleId)!.push(slot)
    }

    const result: CalendarSlot[] = []
    for (const scheduleSlots of bySchedule.values()) {
      const sorted = [...scheduleSlots].sort((a, b) => this.toMs(a.start) - this.toMs(b.start))
      let i = 0
      while (i < sorted.length) {
        const current = sorted[i]
        if (current.type !== 'appointment') {
          result.push(current)
          i++
          continue
        }

        const run: AppointmentSlot[] = [current]
        let j = i + 1
        while (j < sorted.length) {
          const next = sorted[j]
          if (next.type !== 'appointment') break
          if (!this.canGroupAppointments(run[run.length - 1], next, scheduleGrids)) break
          run.push(next)
          j++
        }

        if (run.length >= 2) {
          result.push(this.buildMergedAppointment(run))
        } else {
          result.push(current)
        }
        i = j
      }
    }
    return result
  }

  private canGroupAppointments(a: AppointmentSlot, b: AppointmentSlot, scheduleGrids: ScheduleTimeGrid[] = []): boolean {
    if (!a.patient || !b.patient) return false
    if (a.patient.id !== b.patient.id) return false

    const gapMs = this.toMs(b.start) - this.toMs(a.end)
    if (gapMs >= -60_000 && gapMs <= 60_000) return true

    // Fit-ins inside the same grid bucket should also group, even when the previous
    // appointment ends well before the next one starts (e.g. a 10min appointment at
    // 07:00 + a fit-in at 07:14 both live inside the 07:00 grid slot).
    if (scheduleGrids.length === 0) return false
    const bucketA = this.findContainingGridSlot(a, scheduleGrids)
    const bucketB = this.findContainingGridSlot(b, scheduleGrids)
    if (!bucketA || !bucketB) return false
    return bucketA.start === bucketB.start && bucketA.end === bucketB.end
  }

  private findContainingGridSlot(
    apt: AppointmentSlot,
    scheduleGrids: ScheduleTimeGrid[],
  ): { start: string; end: string } | null {
    const grid = scheduleGrids.find((g) => g.scheduleId === apt.scheduleId)
    if (!grid) return null
    const aptStartMs = this.toMs(apt.start)
    for (const slot of grid.slots) {
      if (this.toMs(slot.start) <= aptStartMs && aptStartMs < this.toMs(slot.end)) {
        return { start: slot.start, end: slot.end }
      }
    }
    return null
  }

  private buildMergedAppointment(run: AppointmentSlot[]): AppointmentSlot {
    const first = run[0]
    const last = run[run.length - 1]

    const procedureMap = new Map<number, { id: number; name: string }>()
    for (const apt of run) {
      for (const p of apt.procedures ?? []) {
        if (!procedureMap.has(p.id)) procedureMap.set(p.id, p)
      }
    }

    const lockUsersMap = new Map<number, { id: number; name: string }>()
    for (const apt of run) {
      for (const u of apt.lockUsers ?? []) {
        if (!lockUsersMap.has(u.id)) lockUsersMap.set(u.id, u)
      }
    }

    const statusIds = new Set<number>()
    const statusColors: string[] = []
    for (const apt of run) {
      const id = apt.status?.id
      if (id == null || statusIds.has(id)) continue
      statusIds.add(id)
      statusColors.push(getStatusColor(id))
    }

    return {
      ...first,
      end: last.end,
      procedures: Array.from(procedureMap.values()),
      lockUsers: Array.from(lockUsersMap.values()),
      extendedProps: {
        ...(first.extendedProps ?? {}),
        merged: true,
        mergedAppointments: run,
        mergedCount: run.length,
        mixedStatuses: statusIds.size > 1,
        statusColors,
      },
    }
  }

  // --- Time utilities ---

  toMs(iso: string): number {
    return new Date(iso).getTime()
  }

  compareTime(a: string, b: string): number {
    return this.toMs(a) - this.toMs(b)
  }

  isBefore(a: string, b: string): boolean {
    return this.toMs(a) < this.toMs(b)
  }

  isBeforeOrEqual(a: string, b: string): boolean {
    return this.toMs(a) <= this.toMs(b)
  }

  isAfter(a: string, b: string): boolean {
    return this.toMs(a) > this.toMs(b)
  }

  overlaps(a: CalendarSlotBase, b: CalendarSlotBase): boolean {
    return this.toMs(a.start) < this.toMs(b.end) && this.toMs(a.end) > this.toMs(b.start)
  }

  diffMinutes(start: string, end: string): number {
    return (this.toMs(end) - this.toMs(start)) / 60000
  }

  toLocalIso(ms: number): string {
    const d = new Date(ms)
    const pad = (n: number) => n.toString().padStart(2, '0')
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
  }

  normalizeDateToBase(isoDateTime: string, baseDate: string): string {
    const timePart = isoDateTime.split('T')[1]
    if (timePart) return `${baseDate}T${timePart}`
    return isoDateTime
  }

  listDatesInRange(start: string, end: string): string[] {
    if (!start || !end) return []
    const startMs = new Date(`${start}T12:00:00`).getTime()
    const endMs = new Date(`${end}T12:00:00`).getTime()
    if (Number.isNaN(startMs) || Number.isNaN(endMs) || endMs < startMs) return []
    const dayMs = 24 * 60 * 60 * 1000
    const pad = (n: number) => n.toString().padStart(2, '0')
    const dates: string[] = []
    for (let ms = startMs; ms <= endMs; ms += dayMs) {
      const d = new Date(ms)
      dates.push(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`)
    }
    return dates
  }

  extractTime(isoDateTime: string): string {
    const timePart = isoDateTime.split('T')[1]
    if (timePart) return timePart.substring(0, 8)
    const d = new Date(isoDateTime)
    const pad = (n: number) => n.toString().padStart(2, '0')
    return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
  }
}
